import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  Save,
  WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { TipoBadge } from "@/shared/ui/atoms/TipoBadge.js";
import { StatusBadge } from "@/shared/ui/atoms/StatusBadge.js";
import { Avatar } from "@/shared/ui/atoms/Avatar.js";
import { Button } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { uploadImagen } from "@/shared/lib/upload.js";
import { useAuth, useHomePath } from "@/features/auth/index.js";
import {
  getInforme,
  updateInformePayload,
} from "../services/reports.service.js";
import {
  enqueueReport,
  getReport,
  getReportByServerId,
  putReport,
} from "@/features/offline-sync/index.js";
import {
  resolveFromNetworkLoad,
  resolveFromIdbLoad,
} from "./informe-editor-server-id.js";
import { queueFirmaBlobs, resolvePayloadBlobs, syncFinalizedReport } from "./finalizar-helpers.js";
import { TIPO_LABEL } from "../types.js";
import type {
  AcPayload,
  IeDatosGenerales,
  Informe,
  IePayload,
  IsPayload,
  TipoInforme,
} from "../types.js";
import { crearContinuacionLocal } from "./crearContinuacionLocal.js";
import { seedActividadesFromPanel, mergeActividadesFromPanel } from "./is/paso-actividades-seeder.js";
import { toIePayload } from "./ie/ie-payload.js";
import { toIsPayload } from "./is/is-payload.js";
import { toAcPayload } from "./ac/ac-payload.js";
import { validarPasoFn } from "./informe-editor-validation.js";
import {
  seedClienteFromReport,
  seedEmisionDefault,
  correlativoDeNumero,
} from "./paso-datos-generales-helpers.js";
import { PasoDatosGenerales } from "./ie/PasoDatosGenerales.js";
import { PasoEquipos } from "./ie/PasoEquipos.js";
import { PasoPanelFotografico } from "./ie/PasoPanelFotografico.js";
import { PasoProblemas } from "./ie/PasoProblemas.js";
import { PasoActividades } from "./is/PasoActividades.js";
import { PasoConclusiones } from "./is/PasoConclusiones.js";
import { PasoServicioEngrase } from "./is/PasoServicioEngrase.js";
import { PasoFirmas } from "./ac/PasoFirmas.js";
import { PASOS_POR_TIPO } from "./informe-editor-steps.js";

/**
 * Solo `borrador` y `en_cola` son editables. Una vez `sincronizado` (subido a
 * la nube) o `enviado` (entregado), el informe queda de solo lectura: los
 * campos se muestran con sus valores guardados pero deshabilitados.
 */
function esEditable(informe: Informe): boolean {
  return informe.estado === "borrador" || informe.estado === "en_cola";
}

/**
 * Inyecta los campos automáticos comunes a IE e IS en datosGenerales:
 * - `anexo` = correlativo del número (3 dígitos antes del guión, e.g. "002").
 *   Retorna "" cuando el informe no tiene número aún (local/sin sincronizar).
 * - `supervisor` = el ya guardado, o el técnico conectado si aún no hay uno.
 * Compartido entre ambos tipos para no duplicar la lógica de inyección.
 */
function inyectarAutomaticos(
  dg: IeDatosGenerales,
  numero: string,
  nombreUsuario: string,
): IeDatosGenerales {
  return {
    ...dg,
    // correlativoDeNumero returns "" for local drafts (no number yet);
    // PasoDatosGenerales renders the placeholder "—" when the field is empty.
    anexo: correlativoDeNumero(numero),
    supervisor: dg.supervisor || nombreUsuario,
  };
}

/**
 * Uploads a signature to R2 ONLY if it is a local dataURL (freshly drawn).
 * Passes through http URLs (already uploaded), empty strings, and idb-blob:
 * placeholders (queued for R2 upload on reconnect by blob-queue / flush).
 *
 * S3-T7: migrated from Cloudinary to R2 presign+PUT via uploadImagen.
 */
async function subirFirma(v: string): Promise<string> {
  if (!v || !v.startsWith("data:")) return v; // http url, empty, or idb-blob: placeholder
  const blob = await (await fetch(v)).blob();
  const file = new File([blob], "firma.png", { type: "image/png" });
  return uploadImagen(file, "firmas");
}

export function InformeEditor() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Read-only view (?ver=1): show every step's saved values but locked, even for
  // editable (borrador/en_cola) reports. Used by "Ver formulario" in the detail page.
  const soloLectura = searchParams.get("ver") === "1";
  const navigate = useNavigate();
  const { user } = useAuth();
  const homePath = useHomePath();
  const nombreUsuario = user?.nombre ?? "";

  const [informe, setInforme] = useState<Informe | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paso, setPaso] = useState(0);
  // ponytail: finalizar() now redirects to /informe/:id/detalle, so the inline
  // "completado" success screen below is dead (always false). Kept to keep this
  // diff surgical; remove the screen + these flags in a follow-up cleanup.
  const [completado] = useState(false);
  const [sincronizado] = useState(false);
  const [creandoCont, setCreandoCont] = useState(false);

  // Payload completo en estado: IE, IS o AC según el tipo del informe.
  const [ie, setIe] = useState<IePayload | null>(null);
  const [is, setIs] = useState<IsPayload | null>(null);
  const [ac, setAc] = useState<AcPayload | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [navAbierto, setNavAbierto] = useState(false);

  // Fix 1: resolved serverId and localId from the load path.
  // The old derivation (informe.id !== id ? informe.id : undefined) was always
  // undefined when the route is /informe/<serverId> because informe.id === id.
  // These are set in load() and used by guardar(), autoGuardarLocal(), finalizar().
  const [resolvedServerId, setResolvedServerId] = useState<string | undefined>(undefined);
  const [resolvedLocalId, setResolvedLocalId] = useState<string>("");

  // IS: al entrar al paso 3 (Actividades — REQ-8a renumbered from paso 4), seed
  // actividades from panelFotografico if they are empty (S-8b.1, S-8b.2, S-8b.3).
  // seedActividadesFromPanel() is idempotent: returns current unchanged if non-empty
  // so user edits are never clobbered, including across offline/online sessions.
  useEffect(() => {
    if (informe?.tipo === "IS" && paso === 3) {
      setIs((prev) =>
        prev
          ? {
              ...prev,
              actividades: seedActividadesFromPanel(prev.actividades, prev.panelFotografico),
            }
          : prev,
      );
    }
  }, [paso, informe?.tipo]);

  useEffect(() => {
    if (!id) return;
    let activo = true;
    setCargando(true);
    setError(null);

    async function load() {
      // S1: try network first; fall back to local IDB on failure (offline or error).
      let data: import("../types.js").Informe | null = null;
      try {
        data = await getInforme(id!);
        // Fix 1 (network path): data.id IS the serverId. Look up the IDB record by
        // serverId to get the original localId for IDB writes (avoids duplicate rows).
        const cachedLocal = await getReportByServerId(id!).catch(() => undefined);
        const resolution = resolveFromNetworkLoad(id!, cachedLocal);
        setResolvedServerId(resolution.serverId);
        setResolvedLocalId(resolution.localId);
      } catch {
        // Network failed — attempt IDB fallback (id may be localId or serverId).
        // Fix 4: try getReport(id) first (localId lookup), then getReportByServerId(id)
        // so offline open by serverId also works (mirrors InformeDetalle.tsx:144).
        const local = (await getReport(id!)) ?? (await getReportByServerId(id!));
        if (local) {
          // Fix 1 (IDB path): resolveFromIdbLoad extracts serverId/localId correctly.
          const resolution = resolveFromIdbLoad(local);
          setResolvedServerId(resolution.serverId);
          setResolvedLocalId(resolution.localId);
          // Synthesize an Informe-shaped object from the local record so the editor
          // can render without changes to its internal state shape.
          data = {
            id: local.serverId ?? local.localId,
            tipo: local.tipo,
            numero: local.numero ?? "(sin número)",
            cliente: local.cliente ?? "",
            clienteId: local.clienteId ?? null,
            estado: local.estado,
            autorId: "",
            parentId: local.parentLocalId ?? null,
            payload: local.payload as Record<string, unknown>,
            pdfUrl: null,
            createdAt: local.updatedAt,
            updatedAt: local.updatedAt,
          };
        }
      }

      if (!activo) return;
      if (!data) {
        setError("No se pudo cargar el informe (sin red y sin caché local).");
        setCargando(false);
        return;
      }

      setInforme(data);
      if (data.tipo === "IE") {
        const base = toIePayload(data.payload, data.cliente);
        const dg0 = inyectarAutomaticos(base.datosGenerales, data.numero, nombreUsuario);
        const dg1 = seedClienteFromReport(dg0, data.cliente); // REQ-7a
        const dg2 = seedEmisionDefault(dg1); // REQ-7b
        setIe({ ...base, datosGenerales: dg2 });
      } else if (data.tipo === "IS") {
        const base = toIsPayload(data.payload, data.cliente);
        const dg0 = inyectarAutomaticos(base.datosGenerales, data.numero, nombreUsuario);
        const dg1 = seedClienteFromReport(dg0, data.cliente); // REQ-7a
        const dg2 = seedEmisionDefault(dg1); // REQ-7b
        setIs({ ...base, datosGenerales: dg2 });
      } else if (data.tipo === "AC") {
        const base = toAcPayload(data.payload, data.cliente);
        const dg0 = inyectarAutomaticos(base.datosGenerales, data.numero, nombreUsuario);
        const dg1 = seedClienteFromReport(dg0, data.cliente); // REQ-7a
        const dg2 = seedEmisionDefault(dg1); // REQ-7b
        setAc({ ...base, datosGenerales: dg2 });
      }
      setCargando(false);
    }

    void load();
    return () => {
      activo = false;
    };
  }, [id, nombreUsuario]);

  const editable = informe ? esEditable(informe) && !soloLectura : false;

  /**
   * Guarda el payload COMPLETO (IE o IS según el tipo). No dependemos de merge
   * en el backend: siempre se envía el payload entero vía updateInformePayload.
   *
   * Fix 1+2: uses resolvedServerId (set during load) instead of the broken derivation
   * `informe.id !== id ? informe.id : undefined` which was always undefined when
   * the route is /informe/<serverId>.
   *
   * Decision matrix:
   *   online  + serverId → PATCH server + write IDB (Fix 2: also cache to IDB)
   *   offline + serverId → write IDB only (graceful offline save, no error shown)
   *   no serverId        → write IDB only (local-only borrador, finalizar() enqueues)
   */
  const guardar = useCallback(
    async (opts?: { silent?: boolean }): Promise<boolean> => {
      if (!id || !editable || !informe) return true;
      let payload: IePayload | IsPayload | AcPayload | null = ie ?? is ?? ac;
      if (!payload) return true;
      setGuardando(true);
      setError(null);

      // Fix 1: use the correctly resolved serverId (not the broken id-comparison derivation).
      const serverId = resolvedServerId;
      // Fix 1: use the correct localId for IDB writes (not always the route param).
      const localId = resolvedLocalId || id;

      if (!serverId) {
        // Local-only report (no serverId): IDB only. finalizar() will POST to server.
        try {
          await putReport({
            localId,
            tipo: informe.tipo,
            clienteId: informe.clienteId ?? undefined,
            cliente: informe.cliente,
            parentLocalId: informe.parentId ?? undefined,
            estado: "borrador",
            payload: payload as unknown as Record<string, unknown>,
            updatedAt: new Date().toISOString(),
          });
          if (!opts?.silent) toast.success("Borrador guardado");
          return true;
        } catch (err) {
          const msg = friendlyMessage(err);
          setError(msg);
          toast.error(msg);
          return false;
        } finally {
          setGuardando(false);
        }
      }

      if (!navigator.onLine) {
        // Fix 2 (offline path): gracefully write to IDB instead of trying PATCH and failing.
        // The auto-save already does this every 15s; this path handles the explicit "Guardar" click.
        try {
          await putReport({
            localId,
            serverId,
            numero: informe.numero !== "(sin número)" ? informe.numero : undefined,
            tipo: informe.tipo,
            clienteId: informe.clienteId ?? undefined,
            cliente: informe.cliente,
            parentLocalId: informe.parentId ?? undefined,
            estado: informe.estado,
            payload: payload as unknown as Record<string, unknown>,
            updatedAt: new Date().toISOString(),
          });
          if (!opts?.silent) toast.success("Sin señal — cambios guardados localmente");
          return true;
        } catch (err) {
          const msg = friendlyMessage(err);
          setError(msg);
          toast.error(msg);
          return false;
        } finally {
          setGuardando(false);
        }
      }

      // Fix 2 (online path): PATCH server AND write IDB for local cache.
      try {
        // AC: las firmas se dibujan como dataURL local; recién acá (al guardar)
        // se suben a R2 y se reemplazan por la URL.
        if (ac) {
          const tecnico = await subirFirma(ac.firmas.tecnico);
          const clienteUrl = ac.firmas.cliente ? await subirFirma(ac.firmas.cliente) : undefined;
          const acFinal: AcPayload = {
            ...ac,
            firmas: clienteUrl ? { tecnico, cliente: clienteUrl } : { tecnico },
          };
          setAc(acFinal);
          payload = acFinal;
        }
        const actualizado = await updateInformePayload(
          serverId,
          payload as unknown as Record<string, unknown>,
        );
        setInforme(actualizado);
        // Fix 2: also write to IDB so the latest draft is available offline.
        await putReport({
          localId,
          serverId,
          numero: informe.numero !== "(sin número)" ? informe.numero : undefined,
          tipo: informe.tipo,
          clienteId: informe.clienteId ?? undefined,
          cliente: informe.cliente,
          parentLocalId: informe.parentId ?? undefined,
          estado: informe.estado,
          payload: payload as unknown as Record<string, unknown>,
          updatedAt: new Date().toISOString(),
        });
        if (!opts?.silent) toast.success("Borrador guardado");
        return true;
      } catch (err) {
        const msg = friendlyMessage(err);
        setError(msg);
        toast.error(msg);
        return false;
      } finally {
        setGuardando(false);
      }
    },
    [id, ie, is, ac, editable, informe, resolvedServerId, resolvedLocalId],
  );

  // ── Auto-save every 15s (local-first, no network) ──────────────────────────
  // Persists the in-progress payload to IDB so field work survives a crash/reload
  // and offline edits are never lost. NEVER hits the network or re-uploads firmas
  // (that happens on explicit "Guardar borrador" / finalizar). estado is preserved.
  const [autoguardado, setAutoguardado] = useState<Date | null>(null);
  const dirtyRef = useRef(false);
  // Re-entrancy guard for finalizar() (single-flight, like flush.ts's `flushing`).
  const finalizandoRef = useRef(false);

  useEffect(() => {
    // Any payload edit marks the draft dirty for the next auto-save tick.
    dirtyRef.current = true;
  }, [ie, is, ac]);

  const autoGuardarLocal = useCallback(async () => {
    if (!id || !editable || !informe) return;
    const payload = ie ?? is ?? ac;
    if (!payload) return;
    // Fix 1: use correctly resolved serverId and localId (not the broken derivation).
    const serverId = resolvedServerId;
    const localId = resolvedLocalId || id;
    await putReport({
      localId,
      serverId,
      numero: informe.numero && informe.numero !== "(sin número)" ? informe.numero : undefined,
      tipo: informe.tipo,
      clienteId: informe.clienteId ?? undefined,
      cliente: informe.cliente,
      parentLocalId: informe.parentId ?? undefined,
      estado: informe.estado,
      payload: payload as unknown as Record<string, unknown>,
      updatedAt: new Date().toISOString(),
    });
    setAutoguardado(new Date());
  }, [id, editable, informe, ie, is, ac, resolvedServerId, resolvedLocalId]);

  useEffect(() => {
    if (!id || !editable) return;
    const t = setInterval(() => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      void autoGuardarLocal();
    }, 15_000);
    return () => clearInterval(t);
  }, [id, editable, autoGuardarLocal]);

  const pasos = informe ? PASOS_POR_TIPO[informe.tipo] : [];
  const total = pasos.length;
  const pasoActual = pasos[paso] ?? "";
  const progreso = total > 0 ? ((paso + 1) / total) * 100 : 0;
  const esIE = informe?.tipo === "IE";
  const esIS = informe?.tipo === "IS";
  const esAC = informe?.tipo === "AC";
  // IE, IS y AC comparten el flujo de guardado (payload completo + autosave).
  const tienePayload = esIE || esIS || esAC;

  /**
   * Valida el paso actual antes de avanzar. Devuelve mensaje de error o null.
   * Solo aplica en modo editable.
   *
   * REQ-6: the ONLY blocking check is AC paso 4 firma gate. All other field
   * completeness checks have been removed (S-6.1..S-6.6).
   */
  function validarPaso(): string | null {
    const tipo = informe?.tipo ?? "IE";
    return validarPasoFn({ tipo, ie, is, ac, paso });
  }

  /**
   * Último paso: guarda (silencioso) + borrador → en_cola.
   * S1 local-first: writes to IDB + outbox FIRST (optimistic, NFR-2).
   * If online, also tries network path (existing flow preserved).
   * If offline, the IDB write is the success — UI shows "En cola".
   */
  async function finalizar() {
    if (!informe || !id) return;
    // In-flight guard: the drain + PATCH run OUTSIDE flush's single-flight lock,
    // so a second click (double-click / repeated "Finalizar") while one finalize
    // is in flight must be a no-op — same pattern as flush.ts's `flushing` flag.
    if (finalizandoRef.current) return;
    finalizandoRef.current = true;
    try {
      // Fix 1: use correctly resolved localId and serverId (not the broken id-comparison).
      const localId = resolvedLocalId || id;
      const serverId = resolvedServerId;

      // Build the final payload from in-memory editor state.
      let currentPayload: IePayload | IsPayload | AcPayload | null = ie ?? is ?? ac;

      const online = navigator.onLine;

      // AC firmas: the pad holds them as local data: URLs. Queue them to IDB as
      // `idb-blob:` placeholders FIRST — local-first (S1): the report must be fully
      // persisted offline-safe BEFORE any network call, so a finalize is never lost to a
      // flaky connection. The online branch below uploads them to real URLs; if that
      // fails, the report stays en_cola with its blobs queued and flush resolves it later.
      if (ac) {
        currentPayload = await queueFirmaBlobs(ac, localId);
      }

      // Write to local IDB + outbox (optimistic, offline-safe) BEFORE the network.
      // Fix 1: serverId is now correctly resolved; no fallback needed.
      await enqueueReport({
        localId,
        serverId,
        tipo: informe.tipo,
        clienteId: informe.clienteId ?? undefined,
        cliente: informe.cliente,
        parentLocalId: informe.parentId ?? undefined,
        payload: currentPayload as unknown ?? {},
        updatedAt: new Date().toISOString(),
      });

      if (!online) {
        // Offline path: IDB write succeeded → report in queue. Go straight to the
        // detail view, which renders the client-side PDF preview (works offline).
        navigate(`/informe/${id}/detalle`);
        return;
      }

      // Online path. Fix 1: use serverId (not the raw route param id) for network calls,
      // since id may be a localUUID for /informe/<localId> navigation.
      const networkId = serverId ?? id;
      try {
        // Drain ALL queued blobs (firmas AND photos, one machinery) and resolve
        // `idb-blob:` placeholders to real R2 URLs BEFORE the PATCH — the server must
        // never store a placeholder: a later flush /sync carrying the resolved URL is
        // skipped on timestamp (last-write-wins), so the placeholder would stick and
        // render blank. resolvePayloadBlobs also refuses resolution when the injected
        // payload STILL contains placeholders (vacuous drain + stale local draft).
        const drain = await resolvePayloadBlobs(
          localId,
          currentPayload as unknown as Record<string, unknown>,
        );
        const patched = await syncFinalizedReport(networkId, localId, drain);
        if (!patched) {
          // Partial drain (or stale placeholders): NO PATCH and NO discardBlobs — the
          // report stays en_cola with its blobs queued and placeholders intact; flush
          // resolves and syncs them on reconnect.
          toast.info(
            "Algunas fotos no se pudieron subir. El informe quedó en cola y se sincronizará solo.",
          );
        }
        // Go to the detail view: shows the PDF preview + download (server PDF when
        // synced, client-rendered preview offline).
        navigate(`/informe/${networkId}/detalle`);
      } catch (err) {
        // Network failed AFTER the local save — the report is safe (en_cola, firmas
        // queued); flush resolves it on reconnect. Surface the error and show it queued.
        toast.error(friendlyMessage(err));
        navigate(`/informe/${id}/detalle`);
      }
    } finally {
      finalizandoRef.current = false;
    }
  }

  /**
   * Crea un informe vinculado heredando datos del informe actual desde IndexedDB
   * (D3 local-first — works offline and online via the same code path).
   *
   * The user explicitly selects the child type (B-T1): IE can produce IS or AC;
   * IS can only produce AC.
   *
   * // ponytail: reads parent from IDB via crearContinuacionLocal; no network call
   * // for inheritance. The child is enqueued and flush handles the server write.
   */
  async function crearContinuacion(childTipo: Extract<TipoInforme, "IS" | "AC">) {
    if (!informe || !id) return;
    setCreandoCont(true);
    try {
      // Use the localId (id may be a serverId for online reports, but crearContinuacionLocal
      // falls back to by-serverId if needed — use `id` as the lookup key here.
      // For online reports the IDB record was written with serverId; getReport(id) finds it.
      const childLocalId = await crearContinuacionLocal(
        id,
        childTipo,
        informe.clienteId ?? undefined,
        informe.cliente,
      );
      navigate(`/informe/${childLocalId}`);
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setCreandoCont(false);
    }
  }

  async function irSiguiente() {
    if (editable) {
      const err = validarPaso();
      if (err) {
        toast.error(err);
        return;
      }
    }
    if (paso === total - 1) {
      // Last step: editable → finalize (saves + navigates to this report's detail);
      // read-only → go back to the same report's detail (NOT the menu).
      if (editable) await finalizar();
      else navigate(`/informe/${id}/detalle`);
      return;
    }
    setPaso((p) => Math.min(total - 1, p + 1));
  }

  /** Volver: guarda en silencio (si es editable) y navega. */
  async function volver() {
    if (tienePayload && editable) await guardar({ silent: true });
    // ?ver=1 consult opened from the detail page ("Ver formulario") — return
    // to the detail, not the home panel.
    navigate(soloLectura ? `/informe/${id}/detalle` : homePath);
  }

  return (
    <div className="min-h-screen bg-[#e7e6df] flex justify-center py-0 px-0 sm:py-6 sm:px-6">
      {/* ── Card container ──────────────────────────────────────────────── */}
      <div className="w-full max-w-[1800px] bg-[#fdfcf9] rounded-none border-0 sm:rounded-[28px] sm:border sm:border-[#ddd9cc] overflow-hidden flex flex-col shadow-none sm:shadow-sm">

        {/* ── TopAppBar ─────────────────────────────────────────────────── */}
        <header className="flex items-center justify-end px-4 py-3 sm:px-8 sm:py-4 border-b border-[#c8c7bf] bg-[#fdfcf9] shrink-0">
          <button
            type="button"
            onClick={() => void volver()}
            className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741] hover:text-[#1c1b1b] transition-colors"
          >
            <ArrowLeft className="size-4" aria-hidden />
            VOLVER
          </button>
        </header>

        {/* ── Body depending on load state ──────────────────────────────── */}
        {cargando ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-[#c8c7bf]" aria-hidden />
          </div>
        ) : error && !informe ? (
          <div className="flex-1 p-8 text-sm text-destructive">{error}</div>
        ) : informe ? (
          <>
            {/* ── Header info block ──────────────────────────────────────── */}
            <div className="px-4 py-4 sm:px-8 sm:py-8 border-b border-[#c8c7bf] bg-[#fdfcf9] flex flex-col gap-6 shrink-0">
              {/* Row 1: tipo chip + numero + status + read-only badge */}
              <div className="flex flex-wrap items-center gap-4">
                <TipoBadge tipo={informe.tipo} />
                <h1 className="font-bold text-[28px] leading-[36px] tracking-tight text-[#1c1b1b]">
                  {informe.numero}
                </h1>
                <StatusBadge estado={informe.estado} />
                {!editable && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c8c7bf] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741]">
                    <Lock className="size-3" aria-hidden />
                    Solo lectura
                  </span>
                )}
              </div>
              {/* Row 2: avatar + client name + tipo label + B-T1 buttons */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Avatar name={informe.cliente} className="size-12 rounded-lg" />
                  <div>
                    <div className="text-[18px] font-bold text-[#1c1b1b]">{informe.cliente}</div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741]">
                      {TIPO_LABEL[informe.tipo]}
                    </div>
                  </div>
                </div>
                {/* B-T1: child-type selection buttons — IE shows IS + AC; IS shows AC only */}
                {/* S-1.4, S-1.5, S-1.6 */}
                {(informe.tipo === "IE" || informe.tipo === "IS") &&
                  (informe.estado === "sincronizado" || informe.estado === "enviado") && (
                    <div className="flex flex-wrap items-center gap-2">
                      {informe.tipo === "IE" && (
                        <Button
                          type="button"
                          size="sm"
                          disabled={creandoCont}
                          onClick={() => void crearContinuacion("IS")}
                          className="gap-1.5 bg-brand-accent font-mono text-[0.625rem] uppercase tracking-[0.12em] text-brand-accent-foreground hover:bg-brand-accent/90"
                        >
                          {creandoCont ? (
                            <Loader2 className="size-3.5 animate-spin" aria-hidden />
                          ) : (
                            <ChevronRight className="size-3.5" aria-hidden />
                          )}
                          Crear IS vinculado
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        disabled={creandoCont}
                        onClick={() => void crearContinuacion("AC")}
                        className="gap-1.5 border-[#c8c7bf] font-mono text-[0.625rem] uppercase tracking-[0.12em] text-[#474741] hover:bg-[#e5e2e1]"
                        variant="outline"
                      >
                        {creandoCont ? (
                          <Loader2 className="size-3.5 animate-spin" aria-hidden />
                        ) : (
                          <ChevronRight className="size-3.5" aria-hidden />
                        )}
                        Crear AC vinculado
                      </Button>
                    </div>
                  )}
              </div>
            </div>

            {/* ── Main: stepper aside + right content ───────────────────── */}
            <div className="flex flex-1 min-h-0 overflow-hidden">

              {/* Left stepper — desktop only */}
              <aside
                className="hidden md:flex w-64 shrink-0 border-r border-[#c8c7bf] py-8 flex-col gap-1"
                aria-label="Pasos del informe"
              >
                {pasos.map((nombre, i) => {
                  const active = i === paso;
                  return (
                    <button
                      key={nombre}
                      type="button"
                      aria-current={active ? "step" : undefined}
                      onClick={() => setPaso(i)}
                      className={cn(
                        "w-full flex items-center gap-4 px-6 py-3 border-l-4 text-left transition-colors",
                        active
                          ? "bg-[#f4f3ee] border-brand-accent"
                          : "border-transparent hover:bg-[#f4f3ee]",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                          active
                            ? "bg-brand-accent text-brand-accent-foreground"
                            : "bg-[#e5e2e1] text-[#474741]",
                        )}
                      >
                        {i + 1}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] font-bold uppercase tracking-[0.12em] leading-tight",
                          active ? "text-[#1c1b1b]" : "text-[#474741]",
                        )}
                      >
                        {nombre}
                      </span>
                    </button>
                  );
                })}
              </aside>

              {/* Right content panel */}
              <div className="flex-1 flex flex-col overflow-y-auto min-w-0">

                {/* Mobile: step navigation bar */}
                <div className="flex items-center gap-1 border-b border-[#c8c7bf] p-2 md:hidden">
                  <button
                    type="button"
                    onClick={() => setPaso((p) => Math.max(0, p - 1))}
                    disabled={paso === 0}
                    aria-label="Paso anterior"
                    className="rounded-md p-2 text-[#474741] transition-colors hover:bg-[#f4f3ee] hover:text-[#1c1b1b] disabled:opacity-30"
                  >
                    <ChevronLeft className="size-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setNavAbierto((v) => !v)}
                    className="flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-[#f4f3ee]"
                  >
                    <span
                      aria-hidden
                      className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-accent text-[10px] font-bold text-brand-accent-foreground"
                    >
                      {paso + 1}
                    </span>
                    <span className="truncate text-[#1c1b1b]">{pasoActual}</span>
                    <span className="shrink-0 text-[#474741]">
                      {paso + 1}/{total}
                    </span>
                    <ChevronDown
                      className={cn(
                        "size-3.5 shrink-0 text-[#474741] transition-transform duration-200",
                        navAbierto && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaso((p) => Math.min(total - 1, p + 1))}
                    disabled={paso === total - 1}
                    aria-label="Paso siguiente"
                    className="rounded-md p-2 text-[#474741] transition-colors hover:bg-[#f4f3ee] hover:text-[#1c1b1b] disabled:opacity-30"
                  >
                    <ChevronRight className="size-4" aria-hidden />
                  </button>
                </div>

                {/* Mobile: collapsible step list */}
                {navAbierto && (
                  <nav
                    className="flex flex-col gap-1 border-b border-[#c8c7bf] p-2 md:hidden"
                    aria-label="Todos los pasos"
                  >
                    {pasos.map((nombre, i) => {
                      const active = i === paso;
                      return (
                        <button
                          key={nombre}
                          type="button"
                          aria-current={active ? "step" : undefined}
                          onClick={() => {
                            setPaso(i);
                            setNavAbierto(false);
                          }}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.12em] transition-colors",
                            active
                              ? "bg-[#f4f3ee] text-[#1c1b1b]"
                              : "text-[#474741] hover:bg-[#f4f3ee] hover:text-[#1c1b1b]",
                          )}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              "flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                              active
                                ? "bg-brand-accent text-brand-accent-foreground"
                                : "bg-[#e5e2e1] text-[#474741]",
                            )}
                          >
                            {i + 1}
                          </span>
                          {nombre}
                        </button>
                      );
                    })}
                  </nav>
                )}

                {/* Sticky step header */}
                <div className="px-4 py-3 sm:px-10 sm:py-6 border-b border-[#c8c7bf] flex flex-col gap-2 sticky top-0 bg-[#fdfcf9] z-10 shrink-0">
                  <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="flex items-center gap-6 flex-1 min-w-0">
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741]">
                        PASO {paso + 1}/{total}
                      </span>
                      <div
                        className="flex-1 h-2 bg-[#e5e2e1] rounded-full overflow-hidden min-w-16"
                        role="progressbar"
                        aria-valuenow={paso + 1}
                        aria-valuemin={1}
                        aria-valuemax={total}
                      >
                        <div
                          className="h-full bg-brand-accent rounded-full transition-all duration-300"
                          style={{ width: `${progreso}%` }}
                        />
                      </div>
                    </div>
                    {tienePayload && editable && (
                      <button
                        type="button"
                        disabled={guardando}
                        onClick={() => void guardar()}
                        className="flex shrink-0 items-center gap-2 px-5 py-2 border border-[#c8c7bf] rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-[#1c1b1b] hover:bg-[#e5e2e1] transition-colors disabled:opacity-50"
                      >
                        <Save className="size-4" aria-hidden />
                        GUARDAR BORRADOR
                      </button>
                    )}
                  </div>
                  {/* Autosave status — own line so it never gets squeezed next to the bar/button. */}
                  {tienePayload && editable && (guardando || autoguardado) && (
                    <div className="flex justify-end">
                      {guardando ? (
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741]">
                          <Loader2 className="size-3.5 animate-spin" aria-hidden />
                          Guardando…
                        </span>
                      ) : autoguardado ? (
                        <span className="inline-flex items-center gap-1.5 text-[10px] text-[#474741]">
                          <CheckCircle2 className="size-3.5 text-green-500" aria-hidden />
                          Autoguardado{" "}
                          {autoguardado.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Error banner */}
                {error && informe && (
                  <div className="border-b border-destructive/40 bg-destructive/10 px-6 py-2 text-sm text-destructive shrink-0">
                    {error}
                  </div>
                )}

                {/* Completado screen — replaces step content (always false in practice) */}
                {completado && (
                  <div className="flex flex-col items-center gap-6 px-6 py-16 text-center">
                    <CheckCircle2 className="size-14 text-green-500" aria-hidden />
                    <div className="space-y-2">
                      <h2 className="font-bold text-2xl tracking-tight text-[#1c1b1b]">
                        ¡Informe completado!
                      </h2>
                      <p className="text-sm text-[#474741] max-w-sm mx-auto">
                        {sincronizado
                          ? "Sincronizado con el servidor. Ya puedes generar el PDF."
                          : "Guardado en cola — se sincronizará cuando haya conexión."}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em]",
                        sincronizado
                          ? "border-green-500/40 text-green-600"
                          : "border-[#c8c7bf] text-[#474741]",
                      )}
                    >
                      {sincronizado ? (
                        <>
                          <CheckCircle2 className="size-3" aria-hidden /> Sincronizado
                        </>
                      ) : (
                        <>
                          <WifiOff className="size-3" aria-hidden /> En cola
                        </>
                      )}
                    </span>
                    {/* B-T1: child-type buttons on completado screen — S-1.4, S-1.5, S-1.6 */}
                    {informe && (informe.tipo === "IE" || informe.tipo === "IS") && (
                      <div className="flex flex-col items-center gap-2 sm:flex-row">
                        {informe.tipo === "IE" && (
                          <Button
                            type="button"
                            disabled={creandoCont}
                            onClick={() => void crearContinuacion("IS")}
                            className="gap-2 bg-brand-accent font-mono text-xs uppercase tracking-[0.12em] text-brand-accent-foreground hover:bg-brand-accent/90"
                          >
                            {creandoCont && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                            Crear IS vinculado →
                          </Button>
                        )}
                        <Button
                          type="button"
                          disabled={creandoCont}
                          onClick={() => void crearContinuacion("AC")}
                          variant="outline"
                          className="gap-2 border-[#c8c7bf] font-mono text-xs uppercase tracking-[0.12em] text-[#474741] hover:bg-[#e5e2e1]"
                        >
                          {creandoCont && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                          Crear AC vinculado →
                        </Button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => void navigate(homePath)}
                      className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741] underline underline-offset-4 hover:text-[#1c1b1b]"
                    >
                      Volver a mis informes
                    </button>
                  </div>
                )}

                {/* Step content */}
                {!completado && (
                  <div className="p-4 sm:p-10 flex-1">
                    {esIE && ie ? (
                      <>
                        {paso === 0 && (
                          <PasoDatosGenerales
                            value={ie.datosGenerales}
                            onChange={(dg) =>
                              setIe((prev) => (prev ? { ...prev, datosGenerales: dg } : prev))
                            }
                            disabled={!editable}
                          />
                        )}
                        {paso === 1 && (
                          <PasoEquipos
                            value={ie.equipos}
                            onChange={(eq) =>
                              setIe((prev) => (prev ? { ...prev, equipos: eq } : prev))
                            }
                            disabled={!editable}
                          />
                        )}
                        {paso === 2 && (
                          <PasoPanelFotografico
                            value={ie.panelFotografico}
                            localId={id ?? ""}
                            onChange={(pf) =>
                              setIe((prev) => (prev ? { ...prev, panelFotografico: pf } : prev))
                            }
                            onError={setError}
                            disabled={!editable}
                          />
                        )}
                        {paso === 3 && (
                          <PasoProblemas
                            value={ie.problemas}
                            onChange={(pr) =>
                              setIe((prev) => (prev ? { ...prev, problemas: pr } : prev))
                            }
                            disabled={!editable}
                          />
                        )}
                      </>
                    ) : esIS && is ? (
                      <>
                        {paso === 0 && (
                          <PasoDatosGenerales
                            value={is.datosGenerales}
                            onChange={(dg) =>
                              setIs((prev) => (prev ? { ...prev, datosGenerales: dg } : prev))
                            }
                            disabled={!editable}
                            supervisorLabel="Supervisor"
                          />
                        )}
                        {paso === 1 && (
                          <PasoEquipos
                            value={is.equipos}
                            onChange={(eq) =>
                              setIs((prev) => (prev ? { ...prev, equipos: eq } : prev))
                            }
                            disabled={!editable}
                          />
                        )}
                        {/* REQ-8a: Antecedentes step removed. IS steps: 0=DG, 1=Equipos, 2=Panel, 3=Actividades, 4=Conclusiones, 5=Servicio */}
                        {paso === 2 && (
                          <PasoPanelFotografico
                            value={is.panelFotografico}
                            localId={id ?? ""}
                            onChange={(pf) =>
                              setIs((prev) => (prev ? { ...prev, panelFotografico: pf } : prev))
                            }
                            onError={setError}
                            disabled={!editable}
                          />
                        )}
                        {paso === 3 && (
                          <PasoActividades
                            value={is.actividades}
                            onChange={(ac) =>
                              setIs((prev) => (prev ? { ...prev, actividades: ac } : prev))
                            }
                            onError={setError}
                            disabled={!editable}
                            onJalarPanel={() => {
                              const merged = mergeActividadesFromPanel(
                                is.actividades,
                                is.panelFotografico,
                              );
                              if (merged.length === is.actividades.length) {
                                toast.info("No hay descripciones nuevas en el panel fotográfico.");
                                return;
                              }
                              const añadidas = merged.length - is.actividades.length;
                              toast.success(`Se agregaron ${añadidas} actividad(es) del panel.`);
                              setIs((prev) => (prev ? { ...prev, actividades: merged } : prev));
                            }}
                          />
                        )}
                        {paso === 4 && (
                          <PasoConclusiones
                            value={is.conclusionesRecomendaciones}
                            onChange={(c) =>
                              setIs((prev) =>
                                prev ? { ...prev, conclusionesRecomendaciones: c } : prev,
                              )
                            }
                            disabled={!editable}
                          />
                        )}
                        {paso === 5 && (
                          <PasoServicioEngrase
                            tipoServicio={is.tipoServicio}
                            engrase={is.engrase}
                            onTipoServicioChange={(t) =>
                              setIs((prev) => (prev ? { ...prev, tipoServicio: t } : prev))
                            }
                            onEngraseChange={(e) =>
                              setIs((prev) => (prev ? { ...prev, engrase: e } : prev))
                            }
                            disabled={!editable}
                          />
                        )}
                      </>
                    ) : esAC && ac ? (
                      <>
                        {paso === 0 && (
                          <PasoDatosGenerales
                            value={ac.datosGenerales}
                            onChange={(dg) =>
                              setAc((prev) => (prev ? { ...prev, datosGenerales: dg } : prev))
                            }
                            disabled={!editable}
                            supervisorLabel="Supervisor"
                          />
                        )}
                        {paso === 1 && (
                          <PasoEquipos
                            value={ac.equipos}
                            onChange={(eq) =>
                              setAc((prev) => (prev ? { ...prev, equipos: eq } : prev))
                            }
                            disabled={!editable}
                          />
                        )}
                        {paso === 2 && (
                          <PasoActividades
                            value={ac.actividades}
                            onChange={(act) =>
                              setAc((prev) => (prev ? { ...prev, actividades: act } : prev))
                            }
                            onError={setError}
                            disabled={!editable}
                          />
                        )}
                        {paso === 3 && (
                          <PasoConclusiones
                            value={ac.conclusionesRecomendaciones}
                            onChange={(c) =>
                              setAc((prev) =>
                                prev ? { ...prev, conclusionesRecomendaciones: c } : prev,
                              )
                            }
                            disabled={!editable}
                          />
                        )}
                        {paso === 4 && (
                          <PasoFirmas
                            value={ac.firmas}
                            onChange={(f) =>
                              setAc((prev) => (prev ? { ...prev, firmas: f } : prev))
                            }
                            onError={setError}
                            disabled={!editable}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center">
                        <h2 className="font-bold text-xl tracking-tight text-[#1c1b1b]">
                          {pasoActual}
                        </h2>
                        <p className="text-sm text-[#474741]">
                          Próximamente — campos del formulario.
                        </p>
                        <span className="mt-2 rounded-full border border-[#c8c7bf] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741]">
                          En construcción
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* end right content panel */}
            </div>
            {/* end main body */}

            {/* ── Footer navigation ──────────────────────────────────────── */}
            {!completado && (
              <footer className="flex items-center justify-between px-4 py-3 sm:px-8 sm:py-4 border-t border-[#c8c7bf] bg-[#f7f3f2] shrink-0">
                <button
                  type="button"
                  disabled={paso === 0 || guardando}
                  onClick={() => setPaso((p) => Math.max(0, p - 1))}
                  className="flex items-center gap-2 px-6 py-2 border border-[#c8c7bf] rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741] hover:bg-[#e5e2e1] transition-colors disabled:opacity-40"
                >
                  <ChevronLeft className="size-4" aria-hidden />
                  ATRÁS
                </button>
                <button
                  type="button"
                  disabled={guardando}
                  onClick={() => void irSiguiente()}
                  className="flex items-center gap-2 px-8 py-3 bg-brand-accent rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-brand-accent-foreground hover:opacity-90 transition-all active:scale-95 duration-150 disabled:opacity-50"
                >
                  {guardando && <Loader2 className="size-4 animate-spin" aria-hidden />}
                  {paso === total - 1 ? (
                    editable ? (
                      <>
                        FINALIZAR
                        <CheckCircle2 className="size-4" aria-hidden />
                      </>
                    ) : (
                      <>
                        VER DETALLE
                        <ChevronRight className="size-4" aria-hidden />
                      </>
                    )
                  ) : (
                    <>
                      SIGUIENTE
                      <ChevronRight className="size-4" aria-hidden />
                    </>
                  )}
                </button>
              </footer>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
